import { useEffect, useState } from "react";
import { api, adminUpdatePage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, ChevronDown } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import MediaPicker from "../../components/MediaPicker";

const SLUGS = ["home", "about", "areas-covered", "contact", "packages", "packages-standard", "packages-10-hour", "packages-20-hour", "packages-bespoke", "privacy", "terms", "refund-policy"];

const BLOCK_TYPES = {
  hero: {
    label: "Hero / banner (home page only)",
    fields: [
      { key: "eyebrow", type: "text", label: "Eyebrow (small label)" },
      { key: "title", type: "text", label: "Main heading" },
      { key: "subtitle", type: "textarea", label: "Subtitle" },
      { key: "image", type: "image", label: "Background image (overrides default)" },
      { key: "image_alt", type: "text", label: "Background image alt text" },
      { key: "cta_primary", type: "kv", label: "Primary CTA", subFields: [{ key: "label" }, { key: "href" }] },
      { key: "cta_secondary", type: "kv", label: "Secondary CTA", subFields: [{ key: "label" }, { key: "href" }] },
    ],
  },
  richtext: {
    label: "Rich text block",
    fields: [
      { key: "title", type: "text", label: "Heading (optional)" },
      { key: "body", type: "richtext", label: "Body" },
    ],
  },
  intro: {
    label: "Intro (text + side image)",
    fields: [
      { key: "eyebrow", type: "text", label: "Eyebrow (small label)" },
      { key: "title", type: "text", label: "Heading" },
      { key: "body", type: "richtext", label: "Body" },
      { key: "image", type: "image", label: "Side image" },
      { key: "image_alt", type: "text", label: "Image alt text" },
    ],
  },
  image: {
    label: "Image",
    fields: [
      { key: "src", type: "image", label: "Image" },
      { key: "alt", type: "text", label: "Alt text" },
      { key: "caption", type: "text", label: "Caption (optional)" },
    ],
  },
  steps: {
    label: "Steps / numbered cards",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "items", type: "items", label: "Steps", itemFields: [
        { key: "step", type: "text", label: "Step number" },
        { key: "title", type: "text", label: "Title" },
        { key: "body", type: "textarea", label: "Body" },
      ]},
    ],
  },
  features: {
    label: "Features list",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "items", type: "items", label: "Features", itemFields: [
        { key: "title", type: "text", label: "Title" },
        { key: "body", type: "textarea", label: "Body" },
      ]},
    ],
  },
  cta: {
    label: "Call-to-action banner",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "subtitle", type: "text", label: "Subtitle" },
      { key: "cta_label", type: "text", label: "Button label" },
      { key: "cta_to", type: "text", label: "Button link (e.g. /book)" },
    ],
  },
  contact: {
    label: "Contact details",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "body", type: "textarea", label: "Body" },
      { key: "phone", type: "text", label: "Phone" },
      { key: "email", type: "text", label: "Email" },
    ],
  },
  enquiry_form: {
    label: "Enquiry form (name / email / phone / message)",
    fields: [
      { key: "eyebrow", type: "text", label: "Eyebrow (small label)" },
      { key: "title", type: "text", label: "Heading" },
      { key: "body", type: "textarea", label: "Intro text (left column)" },
      { key: "phone", type: "text", label: "Phone shown next to form" },
      { key: "email", type: "text", label: "Email shown next to form" },
      { key: "cta_label", type: "text", label: "Submit button label" },
    ],
  },
  two_col: {
    label: "Two-column rich text",
    fields: [
      { key: "title", type: "text", label: "Heading (spans both columns, optional)" },
      { key: "left", type: "richtext", label: "Left column" },
      { key: "right", type: "richtext", label: "Right column" },
    ],
  },
  icon_features: {
    label: "Icon features (4-up row)",
    fields: [
      { key: "title", type: "text", label: "Heading (optional)" },
      { key: "items", type: "icon_features_items", label: "Items" },
    ],
  },
  checklist: {
    label: "Checklist (filled-tick bullets)",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "subtitle", type: "text", label: "Subtitle (optional)" },
      { key: "items", type: "list_items", label: "Bullet items" },
    ],
  },
  package_cards: {
    label: "Package cards (Packages page)",
    fields: [
      { key: "title", type: "text", label: "Heading (optional)" },
      { key: "items", type: "package_cards_items", label: "Cards" },
    ],
  },
  intensive_hero: {
    label: "Intensive courses — hero",
    fields: [
      { key: "eyebrow", type: "text", label: "Eyebrow (small green label)" },
      { key: "title", type: "text", label: "Main heading" },
      { key: "pass_rate", type: "text", label: "Pass-rate pill (e.g. 92% first-time pass rate)" },
      { key: "subtitle", type: "textarea", label: "Subtitle paragraph" },
      { key: "footnote", type: "textarea", label: "Footnote (small italic text under subtitle)" },
    ],
  },
  test_centres: {
    label: "Intensive courses — test centres (text + map)",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "subtitle", type: "textarea", label: "Intro paragraph" },
      { key: "centres", type: "items", label: "Test centres", itemFields: [
        { key: "name", type: "text", label: "Centre name" },
        { key: "address", type: "text", label: "Address" },
      ]},
    ],
  },
  intensive_steps: {
    label: "Intensive courses — numbered steps",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "subtitle", type: "text", label: "Subtitle (optional)" },
      { key: "items", type: "items", label: "Steps", itemFields: [
        { key: "step", type: "text", label: "Step number (e.g. 01)" },
        { key: "title", type: "text", label: "Step title" },
        { key: "body", type: "textarea", label: "Step body" },
      ]},
    ],
  },
  intensive_courses: {
    label: "Intensive courses — course cards",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "subtitle", type: "textarea", label: "Subtitle" },
      { key: "items", type: "intensive_courses_items", label: "Course cards" },
    ],
  },
  intensive_cta: {
    label: "Intensive courses — closing CTA",
    fields: [
      { key: "title", type: "text", label: "Heading" },
      { key: "subtitle", type: "textarea", label: "Subtitle" },
      { key: "phone", type: "text", label: "Phone number (shown as green tel: link)" },
    ],
  },
};

const NEW_BLOCK_DEFAULTS = {
  hero: { type: "hero", eyebrow: "", title: "", subtitle: "" },
  richtext: { type: "richtext", title: "", body: "<p></p>" },
  intro: { type: "intro", title: "", body: "<p></p>", image: "", image_alt: "" },
  image: { type: "image", src: "", alt: "", caption: "" },
  steps: { type: "steps", title: "", items: [{ step: "01", title: "", body: "" }] },
  features: { type: "features", title: "", items: [{ title: "", body: "" }] },
  cta: { type: "cta", title: "", subtitle: "", cta_label: "Book your lessons", cta_to: "/packages" },
  contact: { type: "contact", title: "", body: "", phone: "", email: "" },
  enquiry_form: { type: "enquiry_form", eyebrow: "Get in touch", title: "Make an enquiry", body: "Send us a quick message — Akram replies within one working day.", phone: "07472 887780", email: "", cta_label: "Send enquiry" },
  two_col: { type: "two_col", title: "", left: "<p></p>", right: "<p></p>" },
  icon_features: {
    type: "icon_features",
    title: "",
    items: [
      { icon: "user-check", title: "Your personal DVSA certified instructor" },
      { icon: "clipboard-check", title: "Free practice theory test with Safe Driving for Life" },
      { icon: "pound-sterling", title: "Competitive driving lesson hourly rates" },
      { icon: "handshake", title: "No quibble refund policy" },
    ],
  },
  checklist: {
    type: "checklist",
    title: "Checklist",
    subtitle: "Just some of the things you'll learn with LIME",
    items: ["Cockpit drill", "Safety checks", "Moving away and stopping", "Mirrors and blind spots"],
  },
  package_cards: {
    type: "package_cards",
    title: "",
    items: [
      { slug: "standard", image: "", headline: "Pay as you go", title: "Standard 2-Hour Lesson",
        blurb: "£39 per hour. Minimum 2-hour lesson (£78). Pay only for what you book.",
        rate_manual: "£39/hr", rate_auto: "£41/hr" },
      { slug: "10-hour", image: "", headline: "Save with a 10-hour block", title: "10 Hour Package",
        blurb: "Book 10 hours up-front and enjoy a reduced hourly rate.",
        rate_manual: "£38/hr", rate_auto: "£40/hr" },
      { slug: "20-hour", image: "", headline: "Best value for new learners", title: "20 Hour Package",
        blurb: "Book 20 hours up-front and enjoy our lowest hourly rate.",
        rate_manual: "£37/hr", rate_auto: "£39/hr" },
      { slug: "bespoke", image: "", headline: "Tailored exactly to you", title: "Bespoke Package",
        blurb: "Bespoke packages available on request — call 07472 887780 to discuss.",
        rate_manual: "On request", rate_auto: "On request" },
    ],
  },
  intensive_hero: {
    type: "intensive_hero",
    eyebrow: "Intensive courses",
    title: "Pass in weeks, not months",
    pass_rate: "",
    subtitle: "Fast-track intensive driving courses tailored for Southampton and Winchester learners.",
    footnote: "",
  },
  test_centres: {
    type: "test_centres",
    title: "Southampton & Winchester test centres",
    subtitle: "We cover both local DVSA test centres and tailor every intensive course around their routes.",
    centres: [
      { name: "Southampton Test Centre", address: "Forster Road, Southampton SO14 6QL" },
      { name: "Winchester Test Centre", address: "Moorside Road, Winchester SO23 0LD" },
    ],
  },
  intensive_steps: {
    type: "intensive_steps",
    title: "How it works",
    subtitle: "",
    items: [
      { step: "01", title: "Enquire", body: "Tell us your availability and target test date." },
      { step: "02", title: "Plan", body: "We put together a bespoke schedule matched to your experience." },
      { step: "03", title: "Pass", body: "Intensive block of lessons, mock test, then your practical." },
    ],
  },
  intensive_courses: {
    type: "intensive_courses",
    title: "Choose your intensive",
    subtitle: "",
    items: [
      {
        key: "advanced", name: "Advanced Intensive — 10 hours", hours: "10 hours", price: "£500",
        best_for: "Learners with recent experience taking a quick refresher.",
        summary: "Short, sharp block of 10 hours before your test.",
        body: "Ideal if you've driven before and need final-polish lessons on test routes.",
        theory_test_pro: { included: false, title: "", subtitle: "", url: "", benefits: [] },
      },
      {
        key: "intermediate", name: "Intermediate Intensive — 20 hours", hours: "20 hours", price: "£1,000",
        best_for: "Learners with ~10 hours of previous practice.",
        summary: "20-hour fast-track block covering all DVSA manoeuvres.",
        body: "Covers all major and minor manoeuvres, independent driving, and show-me/tell-me questions.",
        theory_test_pro: { included: false, title: "", subtitle: "", url: "", benefits: [] },
      },
      {
        key: "beginner", name: "Beginner Intensive — 35 hours", hours: "35 hours", price: "£1,750",
        best_for: "Complete beginners who want to pass as quickly as possible.",
        summary: "Our most comprehensive intensive package — from cockpit drill to test pass.",
        body: "Every lesson is structured around DVSA competencies. Includes mock test and theory support.",
        theory_test_pro: {
          included: true,
          title: "Free Theory Test Pro membership",
          subtitle: "6 months of DVSA-approved revision software, included with the Beginner Intensive.",
          url: "https://www.theorytestpro.co.uk",
          benefits: [
            { title: "Official DVSA questions", body: "Latest revision bank, updated with every DVSA change." },
            { title: "Hazard perception", body: "Interactive video clips mirroring the real test." },
          ],
        },
      },
    ],
  },
  intensive_cta: {
    type: "intensive_cta",
    title: "Ready to lock in your intensive?",
    subtitle: "Call Akram to talk through your availability and target test date.",
    phone: "07472 887780",
  },
};

export default function AdminPagesCMS() {
  const [pages, setPages] = useState({});
  const [activeSlug, setActiveSlug] = useState("home");
  const [mediaTarget, setMediaTarget] = useState(null); // {slug, sectionIdx, fieldKey}
  const [openIdx, setOpenIdx] = useState({}); // collapse state

  useEffect(() => {
    Promise.all(SLUGS.map(s => api.get(`/pages/${s}`).then(r => r.data).catch(() => null)))
      .then(results => {
        const m = {};
        SLUGS.forEach((s, i) => { m[s] = results[i] || { slug: s, title: "", sections: [] }; });
        setPages(m);
      });
  }, []);

  const updatePage = (slug, key, val) => {
    setPages(prev => ({ ...prev, [slug]: { ...prev[slug], [key]: val } }));
  };

  const updateSectionField = (slug, idx, key, val) => {
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || [])];
      sections[idx] = { ...sections[idx], [key]: val };
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };

  const moveSection = (slug, idx, dir) => {
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || [])];
      const target = idx + dir;
      if (target < 0 || target >= sections.length) return prev;
      [sections[idx], sections[target]] = [sections[target], sections[idx]];
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };

  const removeSection = (slug, idx) => {
    if (!window.confirm("Delete this section?")) return;
    setPages(prev => {
      const sections = (prev[slug]?.sections || []).filter((_, i) => i !== idx);
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };

  const addSection = (slug, type) => {
    const def = NEW_BLOCK_DEFAULTS[type];
    if (!def) return;
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || []), { ...def }];
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
    setOpenIdx(prev => ({ ...prev, [`${slug}-${(pages[slug]?.sections?.length || 0)}`]: true }));
  };

  // items array helpers (e.g. steps)
  const addItem = (slug, sIdx, fieldKey, template) => {
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || [])];
      const list = [...(sections[sIdx][fieldKey] || []), { ...template }];
      sections[sIdx] = { ...sections[sIdx], [fieldKey]: list };
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };
  const updateItem = (slug, sIdx, fieldKey, iIdx, k, v) => {
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || [])];
      const list = [...(sections[sIdx][fieldKey] || [])];
      list[iIdx] = { ...list[iIdx], [k]: v };
      sections[sIdx] = { ...sections[sIdx], [fieldKey]: list };
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };
  const moveItem = (slug, sIdx, fieldKey, iIdx, dir) => {
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || [])];
      const list = [...(sections[sIdx][fieldKey] || [])];
      const t = iIdx + dir;
      if (t < 0 || t >= list.length) return prev;
      [list[iIdx], list[t]] = [list[t], list[iIdx]];
      sections[sIdx] = { ...sections[sIdx], [fieldKey]: list };
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };
  const removeItem = (slug, sIdx, fieldKey, iIdx) => {
    setPages(prev => {
      const sections = [...(prev[slug]?.sections || [])];
      const list = (sections[sIdx][fieldKey] || []).filter((_, i) => i !== iIdx);
      sections[sIdx] = { ...sections[sIdx], [fieldKey]: list };
      return { ...prev, [slug]: { ...prev[slug], sections } };
    });
  };

  const save = async (slug) => {
    try {
      const p = pages[slug];
      const payload = {
        slug,
        title: p.title || "",
        meta_title: p.meta_title || "",
        meta_description: p.meta_description || "",
        meta_robots: p.meta_robots || "index,follow",
        canonical_url: p.canonical_url || "",
        schema_json: p.schema_json || "",
        sections: p.sections || [],
      };
      await adminUpdatePage(slug, payload);
      toast.success(`Saved ${slug}`);
    } catch { toast.error("Save failed"); }
  };

  const renderField = (slug, sIdx, field, value) => {
    switch (field.type) {
      case "text":
        return <Input value={value || ""} onChange={e => updateSectionField(slug, sIdx, field.key, e.target.value)} />;
      case "textarea":
        return <Textarea rows={3} value={value || ""} onChange={e => updateSectionField(slug, sIdx, field.key, e.target.value)} />;
      case "richtext":
        return <RichTextEditor value={value || ""} onChange={(html) => updateSectionField(slug, sIdx, field.key, html)} testId={`page-rte-${slug}-${sIdx}-${field.key}`} />;
      case "image":
        return (
          <div>
            <div className="flex gap-2">
              <Input value={value || ""} onChange={e => updateSectionField(slug, sIdx, field.key, e.target.value)} placeholder="https://… or pick from library" />
              <Button type="button" variant="outline" className="border-black/15 whitespace-nowrap"
                      onClick={() => setMediaTarget({ slug, sIdx, fieldKey: field.key })}>
                <ImageIcon className="h-4 w-4 mr-1" /> Library
              </Button>
            </div>
            {value && <img src={value} alt="" className="mt-2 max-h-28 rounded-lg border border-black/5" />}
          </div>
        );
      case "kv": {
        const v = value || {};
        return (
          <div className="grid grid-cols-2 gap-2">
            {field.subFields.map(sf => (
              <Input key={sf.key} placeholder={sf.key} value={v[sf.key] || ""}
                     onChange={e => updateSectionField(slug, sIdx, field.key, { ...v, [sf.key]: e.target.value })} />
            ))}
          </div>
        );
      }
      case "items": {
        const list = value || [];
        const template = field.itemFields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});
        return (
          <div className="space-y-2">
            {list.map((item, i) => (
              <div key={i} className="bg-[#F9FAFB] rounded-xl p-3 border border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Item {i + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, -1)} title="Up"><ArrowUp className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                    <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, 1)} title="Down"><ArrowDown className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                    <button type="button" className="p-1 rounded hover:bg-red-50" onClick={() => removeItem(slug, sIdx, field.key, i)} title="Delete"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                  </div>
                </div>
                {field.itemFields.map(sf => (
                  <div key={sf.key} className="mb-2 last:mb-0">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">{sf.label}</Label>
                    {sf.type === "textarea" ? (
                      <Textarea rows={2} value={item[sf.key] || ""} onChange={e => updateItem(slug, sIdx, field.key, i, sf.key, e.target.value)} />
                    ) : (
                      <Input value={item[sf.key] || ""} onChange={e => updateItem(slug, sIdx, field.key, i, sf.key, e.target.value)} />
                    )}
                  </div>
                ))}
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => addItem(slug, sIdx, field.key, template)}>
              <Plus className="h-4 w-4 mr-1" /> Add item
            </Button>
          </div>
        );
      }
      case "icon_features_items": {
        const list = value || [];
        const iconKeys = ["user-check","clipboard-check","pound-sterling","handshake","shield","award","book-open","car","clock","map-pin","users","thumbs-up","star","refresh","check","phone","mail"];
        const template = { icon: "check", title: "" };
        return (
          <div className="space-y-2">
            {list.map((item, i) => (
              <div key={i} className="bg-[#F9FAFB] rounded-xl p-3 border border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">Icon {i + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, -1)}><ArrowUp className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                    <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, 1)}><ArrowDown className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                    <button type="button" className="p-1 rounded hover:bg-red-50" onClick={() => removeItem(slug, sIdx, field.key, i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Icon</Label>
                    <select className="mt-1 w-full h-10 px-2 rounded-lg border border-black/10 bg-white text-sm"
                            value={item.icon || "check"}
                            onChange={e => updateItem(slug, sIdx, field.key, i, "icon", e.target.value)}>
                      {iconKeys.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Label</Label>
                    <Input value={item.title || ""} onChange={e => updateItem(slug, sIdx, field.key, i, "title", e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => addItem(slug, sIdx, field.key, template)}>
              <Plus className="h-4 w-4 mr-1" /> Add icon
            </Button>
          </div>
        );
      }
      case "list_items": {
        // Flat list of strings (or {title} objects). Normalised to strings.
        const list = (value || []).map(x => typeof x === "string" ? x : (x.title || ""));
        const setList = (next) => updateSectionField(slug, sIdx, field.key, next);
        return (
          <div className="space-y-2">
            {list.map((item, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={item} onChange={e => { const n = [...list]; n[i] = e.target.value; setList(n); }} />
                <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => { if (i === 0) return; const n = [...list]; [n[i-1], n[i]] = [n[i], n[i-1]]; setList(n); }}><ArrowUp className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => { if (i === list.length-1) return; const n = [...list]; [n[i+1], n[i]] = [n[i], n[i+1]]; setList(n); }}><ArrowDown className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                <button type="button" className="p-1 rounded hover:bg-red-50" onClick={() => setList(list.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => setList([...list, ""])}>
              <Plus className="h-4 w-4 mr-1" /> Add bullet
            </Button>
          </div>
        );
      }
      case "intensive_courses_items": {
        const list = value || [];
        const template = {
          key: "new-course", name: "", hours: "", price: "",
          best_for: "", summary: "", body: "",
          theory_test_pro: { included: false, title: "", subtitle: "", url: "", benefits: [] },
        };
        // Nested helpers for the theory_test_pro.benefits sub-list.
        const updateCourse = (i, k, v) => updateItem(slug, sIdx, field.key, i, k, v);
        const updateTheory = (i, k, v) => {
          const next = { ...(list[i].theory_test_pro || {}), [k]: v };
          updateCourse(i, "theory_test_pro", next);
        };
        const updateBenefit = (i, bIdx, k, v) => {
          const benefits = [...((list[i].theory_test_pro && list[i].theory_test_pro.benefits) || [])];
          benefits[bIdx] = { ...benefits[bIdx], [k]: v };
          updateTheory(i, "benefits", benefits);
        };
        const addBenefit = (i) => {
          const benefits = [...((list[i].theory_test_pro && list[i].theory_test_pro.benefits) || []), { title: "", body: "" }];
          updateTheory(i, "benefits", benefits);
        };
        const removeBenefit = (i, bIdx) => {
          const benefits = ((list[i].theory_test_pro && list[i].theory_test_pro.benefits) || []).filter((_, j) => j !== bIdx);
          updateTheory(i, "benefits", benefits);
        };
        return (
          <div className="space-y-3">
            {list.map((item, i) => {
              const theory = item.theory_test_pro || {};
              const benefits = theory.benefits || [];
              return (
                <div key={i} className="bg-[#F9FAFB] rounded-xl p-4 border border-black/5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">
                      Course {i + 1} <span className="ml-2 text-[#37B160] normal-case tracking-normal">key: {item.key || "—"}</span>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, -1)} title="Up"><ArrowUp className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                      <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, 1)} title="Down"><ArrowDown className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                      <button type="button" className="p-1 rounded hover:bg-red-50" onClick={() => removeItem(slug, sIdx, field.key, i)} title="Delete"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Key (internal, kebab-case)</Label>
                      <Input value={item.key || ""} onChange={e => updateCourse(i, "key", e.target.value)} placeholder="advanced" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Course name</Label>
                      <Input value={item.name || ""} onChange={e => updateCourse(i, "name", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Hours (shown next to clock icon)</Label>
                      <Input value={item.hours || ""} onChange={e => updateCourse(i, "hours", e.target.value)} placeholder="20 hours" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Price (shown next to £ icon)</Label>
                      <Input value={item.price || ""} onChange={e => updateCourse(i, "price", e.target.value)} placeholder="£1,000" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Best for</Label>
                      <Input value={item.best_for || ""} onChange={e => updateCourse(i, "best_for", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Summary (shown collapsed)</Label>
                      <Textarea rows={2} value={item.summary || ""} onChange={e => updateCourse(i, "summary", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Body (shown when expanded)</Label>
                      <Textarea rows={3} value={item.body || ""} onChange={e => updateCourse(i, "body", e.target.value)} />
                    </div>
                  </div>

                  {/* Theory Test Pro sub-section */}
                  <div className="mt-4 border-t border-black/5 pt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#37B160]"
                        checked={!!theory.included}
                        onChange={e => updateTheory(i, "included", e.target.checked)}
                      />
                      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#15803D]">
                        Include "Theory Test Pro" panel on this card
                      </span>
                    </label>

                    {theory.included && (
                      <div className="mt-3 bg-white border border-[#DCFCE7] rounded-xl p-3 space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Panel title</Label>
                            <Input value={theory.title || ""} onChange={e => updateTheory(i, "title", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Panel subtitle</Label>
                            <Input value={theory.subtitle || ""} onChange={e => updateTheory(i, "subtitle", e.target.value)} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">External URL (visit Theory Test Pro link)</Label>
                            <Input value={theory.url || ""} onChange={e => updateTheory(i, "url", e.target.value)} placeholder="https://www.theorytestpro.co.uk" />
                          </div>
                        </div>

                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Benefits ({benefits.length})</Label>
                          <div className="space-y-2 mt-2">
                            {benefits.map((b, bIdx) => (
                              <div key={bIdx} className="bg-[#F9FAFB] rounded-lg p-2.5 border border-black/5">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Benefit {bIdx + 1}</span>
                                  <button type="button" className="p-1 rounded hover:bg-red-50" onClick={() => removeBenefit(i, bIdx)}><Trash2 className="h-3 w-3 text-red-500" /></button>
                                </div>
                                <Input className="mb-1.5" value={b.title || ""} placeholder="Benefit title" onChange={e => updateBenefit(i, bIdx, "title", e.target.value)} />
                                <Textarea rows={2} value={b.body || ""} placeholder="Short description" onChange={e => updateBenefit(i, bIdx, "body", e.target.value)} />
                              </div>
                            ))}
                            <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => addBenefit(i)}>
                              <Plus className="h-3 w-3 mr-1" /> Add benefit
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={() => addItem(slug, sIdx, field.key, template)}>
              <Plus className="h-4 w-4 mr-1" /> Add course
            </Button>
          </div>
        );
      }
      case "package_cards_items": {
        const list = value || [];
        return (
          <div className="space-y-3">
            {list.map((item, i) => (
              <div key={i} className="bg-[#F9FAFB] rounded-xl p-4 border border-black/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">
                    Card {i + 1} <span className="ml-2 text-[#37B160] normal-case tracking-normal">slug: /packages/{item.slug || "—"}</span>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, -1)} title="Up"><ArrowUp className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                    <button type="button" className="p-1 rounded hover:bg-black/5" onClick={() => moveItem(slug, sIdx, field.key, i, 1)} title="Down"><ArrowDown className="h-3.5 w-3.5 text-[#4B5563]" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={item.image || ""} onChange={e => updateItem(slug, sIdx, field.key, i, "image", e.target.value)} placeholder="https://… or pick from library" />
                      <Button type="button" variant="outline" className="border-black/15 whitespace-nowrap"
                              onClick={() => setMediaTarget({ slug, sIdx, fieldKey: field.key, itemIdx: i, itemKey: "image" })}>
                        <ImageIcon className="h-4 w-4 mr-1" /> Library
                      </Button>
                    </div>
                    {item.image && <img src={item.image} alt="" className="mt-2 max-h-24 rounded-lg border border-black/5" />}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Eyebrow (small label)</Label>
                      <Input value={item.headline || ""} onChange={e => updateItem(slug, sIdx, field.key, i, "headline", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Card title</Label>
                      <Input value={item.title || ""} onChange={e => updateItem(slug, sIdx, field.key, i, "title", e.target.value)} />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Description</Label>
                    <Textarea rows={2} value={item.blurb || ""} onChange={e => updateItem(slug, sIdx, field.key, i, "blurb", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Manual hourly rate</Label>
                    <Input value={item.rate_manual || ""} placeholder="£39/hr" onChange={e => updateItem(slug, sIdx, field.key, i, "rate_manual", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Automatic hourly rate</Label>
                    <Input value={item.rate_auto || ""} placeholder="£41/hr" onChange={e => updateItem(slug, sIdx, field.key, i, "rate_auto", e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const onMediaPick = (item) => {
    if (!mediaTarget) return;
    if (mediaTarget.itemIdx != null && mediaTarget.itemKey) {
      updateItem(mediaTarget.slug, mediaTarget.sIdx, mediaTarget.fieldKey, mediaTarget.itemIdx, mediaTarget.itemKey, item.url);
    } else {
      updateSectionField(mediaTarget.slug, mediaTarget.sIdx, mediaTarget.fieldKey, item.url);
    }
    setMediaTarget(null);
  };

  return (
    <div data-testid="admin-pages-cms">
      <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Pages CMS</h1>
      <p className="text-sm text-[#4B5563]">Build pages with reusable blocks. All text supports formatting, all images come from your media library.</p>

      <Tabs value={activeSlug} onValueChange={setActiveSlug} className="mt-6">
        <TabsList className="bg-white border border-black/5">
          {SLUGS.map(s => <TabsTrigger key={s} value={s} data-testid={`pages-tab-${s}`}>{s}</TabsTrigger>)}
        </TabsList>
        {SLUGS.map(slug => {
          const p = pages[slug] || {};
          return (
            <TabsContent key={slug} value={slug}>
              {/* Page meta */}
              <div className="mt-4 bg-white border border-black/5 rounded-2xl p-6 space-y-3">
                <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Page meta &amp; SEO</h2>
                <Labelled label="Page title (browser tab + h1 fallback)">
                  <Input value={p.title || ""} onChange={e => updatePage(slug, "title", e.target.value)} data-testid={`page-${slug}-title`} />
                </Labelled>
                <Labelled label="Meta title">
                  <Input value={p.meta_title || ""} onChange={e => updatePage(slug, "meta_title", e.target.value)} />
                </Labelled>
                <Labelled label="Meta description">
                  <Textarea rows={2} value={p.meta_description || ""} onChange={e => updatePage(slug, "meta_description", e.target.value)} />
                </Labelled>
                <div className="grid md:grid-cols-2 gap-3">
                  <Labelled label="Meta robots">
                    <Input value={p.meta_robots || "index,follow"} onChange={e => updatePage(slug, "meta_robots", e.target.value)} />
                  </Labelled>
                  <Labelled label="Canonical URL">
                    <Input value={p.canonical_url || ""} onChange={e => updatePage(slug, "canonical_url", e.target.value)} />
                  </Labelled>
                </div>
                <Labelled label="Schema JSON-LD">
                  <Textarea rows={4} value={p.schema_json || ""} onChange={e => updatePage(slug, "schema_json", e.target.value)}
                            placeholder='{"@context":"https://schema.org",...}' />
                </Labelled>
              </div>

              {/* Sections / blocks */}
              <div className="mt-4 bg-white border border-black/5 rounded-2xl p-6">
                <h2 className="text-base font-medium mb-3" style={{fontFamily:"Outfit, sans-serif"}}>Sections</h2>
                <div className="space-y-3">
                  {(p.sections || []).map((s, idx) => {
                    const spec = BLOCK_TYPES[s.type];
                    const key = `${slug}-${idx}`;
                    const open = openIdx[key] ?? false;
                    return (
                      <div key={idx} className="border border-black/5 rounded-2xl overflow-hidden" data-testid={`section-card-${slug}-${idx}`}>
                        <button type="button" onClick={() => setOpenIdx(prev => ({ ...prev, [key]: !prev[key] }))}
                                className="w-full bg-[#F9FAFB] hover:bg-[#F3F4F6] px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <ChevronDown className={`h-4 w-4 text-[#4B5563] transition-transform ${open ? "rotate-180" : ""}`} />
                            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">{s.type}</span>
                            <span className="text-sm text-[#111827] truncate" style={{fontFamily:"Outfit, sans-serif"}}>
                              {s.title || s.eyebrow || spec?.label || "Section"}
                            </span>
                          </div>
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            <button type="button" className="p-1.5 rounded hover:bg-black/5" onClick={() => moveSection(slug, idx, -1)} title="Move up" data-testid={`section-up-${slug}-${idx}`}><ArrowUp className="h-4 w-4 text-[#4B5563]" /></button>
                            <button type="button" className="p-1.5 rounded hover:bg-black/5" onClick={() => moveSection(slug, idx, 1)} title="Move down" data-testid={`section-down-${slug}-${idx}`}><ArrowDown className="h-4 w-4 text-[#4B5563]" /></button>
                            <button type="button" className="p-1.5 rounded hover:bg-red-50" onClick={() => removeSection(slug, idx)} title="Delete" data-testid={`section-delete-${slug}-${idx}`}><Trash2 className="h-4 w-4 text-red-500" /></button>
                          </div>
                        </button>
                        {open && (
                          <div className="p-4 space-y-3 border-t border-black/5 bg-white">
                            {spec ? spec.fields.map(field => (
                              <Labelled key={field.key} label={field.label}>
                                {renderField(slug, idx, field, s[field.key])}
                              </Labelled>
                            )) : (
                              <div className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                                Unknown block type "{s.type}" — you can still keep it or delete it.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(p.sections || []).length === 0 && (
                    <div className="text-sm text-[#9CA3AF] py-6 text-center border border-dashed border-black/10 rounded-xl">No sections yet — add your first block below.</div>
                  )}
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <Select value="" onValueChange={(t) => t && addSection(slug, t)}>
                    <SelectTrigger className="w-64" data-testid={`add-section-${slug}`}><SelectValue placeholder="+ Add a section…" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(BLOCK_TYPES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-[#9CA3AF]">Pick a type to add a new block</span>
                </div>
              </div>

              <div className="mt-4">
                <Button onClick={() => save(slug)} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid={`page-${slug}-save`}>Save page</Button>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      <MediaPicker
        open={!!mediaTarget}
        onClose={() => setMediaTarget(null)}
        onPick={onMediaPick}
      />
    </div>
  );
}

function Labelled({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-[#4B5563]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
